import React, { useCallback, useEffect, useRef } from 'react';
import { css } from '@compiled/react';

import type { SmartLinkDragData } from '@atlaskit/editor-smart-link-draggable';
import { isSmartLinkDrag } from '@atlaskit/editor-smart-link-draggable';
import { token } from '@atlaskit/tokens';
import { dropTargetForElements } from '@atlaskit/pragmatic-drag-and-drop/element/adapter';

import { isSmartLinkDraggingClass, smartLinkDropOverClass } from './smartLinkDroppableConstants.ts';

const EDGE_BORDER_HIDE_THRESHOLD_PX = 8;
const topBorderColorVar = '--smart-link-drop-target-border-top-color';
const bottomBorderColorVar = '--smart-link-drop-target-border-bottom-color';

const smartLinkDropTargetStyles = css({
	width: '100%',
	position: 'relative',
	borderRadius: token('radius.small'),
	'--smart-link-drop-target-border-color': 'transparent',
	[topBorderColorVar]: 'var(--smart-link-drop-target-border-color)',
	[bottomBorderColorVar]: 'var(--smart-link-drop-target-border-color)',
	'&::before': {
		content: '""',
		position: 'absolute',
		inset: 0,
		border: '2px solid var(--smart-link-drop-target-border-color)',
		borderTopColor: `var(${topBorderColorVar})`,
		borderBottomColor: `var(${bottomBorderColorVar})`,
		borderRadius: 'inherit',
		pointerEvents: 'none',
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-values, @atlaskit/ui-styling-standard/no-imported-style-values
	[`body.${isSmartLinkDraggingClass} &`]: {
		'--smart-link-drop-target-border-color': token('color.border.selected'),
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'&.smart-link-drop-over': {
		backgroundColor: token('color.background.selected.hovered'),
		'--smart-link-drop-target-border-color': token('color.border.selected'),
	},
});

interface SmartLinkDropTargetProps {
	onDropped?: (data: SmartLinkDragData) => void;
	children: React.ReactNode;
	className?: string;
	hideHoveredEdgeBorder?: boolean;
}

/**
 * A wrapper component that acts as a smart link drop target.
 * Renders a div with drop target highlight styling, registers Pragmatic DnD
 * drop target behavior, and optionally invokes an onDrop callback.
 *
 * Used by both the page tree container (highlight-only) and shortcuts section (highlight + action).
 */
export function SmartLinkDropTarget({
	onDropped,
	children,
	className,
	hideHoveredEdgeBorder = false,
}: SmartLinkDropTargetProps): React.JSX.Element {
	const ref = useRef<HTMLDivElement>(null);

	const clearHoveredEdgeBorder = useCallback((element: HTMLDivElement) => {
		element.style.removeProperty(topBorderColorVar);
		element.style.removeProperty(bottomBorderColorVar);
	}, []);

	const updateHoveredEdgeBorder = useCallback(
		(element: HTMLDivElement, clientY: number) => {
			if (!hideHoveredEdgeBorder) {
				clearHoveredEdgeBorder(element);
				return;
			}

			const rect = element.getBoundingClientRect();
			const threshold = Math.min(EDGE_BORDER_HIDE_THRESHOLD_PX, rect.height / 2);
			const distanceFromTop = clientY - rect.top;
			const distanceFromBottom = rect.bottom - clientY;
			const isNearTop =
				distanceFromTop >= 0 &&
				distanceFromTop <= threshold &&
				distanceFromTop <= distanceFromBottom;
			const isNearBottom =
				distanceFromBottom >= 0 &&
				distanceFromBottom <= threshold &&
				distanceFromBottom < distanceFromTop;

			if (isNearTop) {
				element.style.setProperty(topBorderColorVar, 'transparent');
				element.style.removeProperty(bottomBorderColorVar);
				return;
			}

			if (isNearBottom) {
				element.style.setProperty(bottomBorderColorVar, 'transparent');
				element.style.removeProperty(topBorderColorVar);
				return;
			}

			clearHoveredEdgeBorder(element);
		},
		[clearHoveredEdgeBorder, hideHoveredEdgeBorder],
	);

	const updateHoveredEdgeBorderFromLocation = useCallback(
		(
			element: HTMLDivElement,
			location?: {
				current?: {
					input?: {
						clientY?: number;
					};
				};
			},
		) => {
			const clientY = location?.current?.input?.clientY;
			if (typeof clientY !== 'number') {
				clearHoveredEdgeBorder(element);
				return;
			}
			updateHoveredEdgeBorder(element, clientY);
		},
		[clearHoveredEdgeBorder, updateHoveredEdgeBorder],
	);

	useEffect(() => {
		const el = ref.current;
		if (!el) {
			return;
		}

		return dropTargetForElements({
			element: el,
			canDrop: ({ source }) => isSmartLinkDrag(source.data.type),
			onDragEnter: (args) => {
				el.classList.add(smartLinkDropOverClass);
				updateHoveredEdgeBorderFromLocation(el, args?.location);
			},
			onDrag: (args) => {
				updateHoveredEdgeBorderFromLocation(el, args?.location);
			},
			onDragLeave: () => {
				el.classList.remove(smartLinkDropOverClass);
				clearHoveredEdgeBorder(el);
			},
			onDrop: ({ source }) => {
				el.classList.remove(smartLinkDropOverClass);
				clearHoveredEdgeBorder(el);
				onDropped?.(source.data as unknown as SmartLinkDragData);
			},
		});
	}, [clearHoveredEdgeBorder, onDropped, updateHoveredEdgeBorderFromLocation]);

	return (
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- passed by parent for layout alignment
		<div ref={ref} css={smartLinkDropTargetStyles} className={className}>
			{children}
		</div>
	);
}
